// pages/tests/MonthlyTestList.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Tooltip,
  Card,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterIcon,
  PictureAsPdf as PdfIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Publish as PublishIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import axios from 'axios';

const MonthlyTestList = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterAcademicYear, setFilterAcademicYear] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  // Sample data - replace with API call
  const sampleTests = [
    {
      id: 1,
      academicYear: '2024-2025',
      classId: 1,
      className: 'Class 10',
      sectionId: 1,
      sectionName: 'A',
      subjectId: 1,
      subjectName: 'Mathematics',
      testName: 'Monthly Test - January',
      testDate: '2024-01-15',
      maxMarks: 100,
      passingMarks: 35,
      instructions: 'Bring your own calculator',
      status: 'PUBLISHED',
      createdBy: 'Admin',
      totalStudents: 45,
      marksEntered: 45
    },
    {
      id: 2,
      academicYear: '2024-2025',
      classId: 1,
      className: 'Class 10',
      sectionId: 2,
      sectionName: 'B',
      subjectId: 2,
      subjectName: 'Science',
      testName: 'Monthly Test - January',
      testDate: '2024-01-20',
      maxMarks: 100,
      passingMarks: 35,
      instructions: 'No electronic devices allowed',
      status: 'OPEN',
      createdBy: 'Teacher1',
      totalStudents: 42,
      marksEntered: 30
    },
    {
      id: 3,
      academicYear: '2024-2025',
      classId: 2,
      className: 'Class 11',
      sectionId: 1,
      sectionName: 'A',
      subjectId: 3,
      subjectName: 'Physics',
      testName: 'Monthly Test - February',
      testDate: '2024-02-10',
      maxMarks: 50,
      passingMarks: 18,
      instructions: '',
      status: 'DRAFT',
      createdBy: 'Admin',
      totalStudents: 38,
      marksEntered: 0
    }
  ];

  useEffect(() => {
    fetchTests();
  }, [page, rowsPerPage, searchTerm, filterStatus, filterClass, filterAcademicYear]);

  const fetchTests = async () => {
    setLoading(true);
    try {
      // Replace with actual API call
      // const response = await axios.get('/api/tests', {
      //   params: { page, limit: rowsPerPage, search: searchTerm, status: filterStatus, ... }
      // });

      // Using sample data for now
      setTimeout(() => {
        setTests(sampleTests);
        setTotalCount(sampleTests.length);
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('Error fetching tests:', error);
      setLoading(false);
    }
  };

  const handleMenuClick = (event, test) => {
    setAnchorEl(event.currentTarget);
    setSelectedTest(test);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTest(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      // await axios.delete(`/api/tests/${selectedTest.id}`);
      setTests(tests.filter(t => t.id !== selectedTest.id));
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting test:', error);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      DRAFT: { color: 'default', icon: <ScheduleIcon /> },
      OPEN: { color: 'warning', icon: <AssessmentIcon /> },
      COMPLETED: { color: 'info', icon: <CheckCircleIcon /> },
      PUBLISHED: { color: 'success', icon: <PublishIcon /> }
    };

    const config = statusConfig[status] || statusConfig.DRAFT;

    return (
      <Chip
        label={status}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  const getProgressPercentage = (entered, total) => {
    if (total === 0) return 0;
    return Math.round((entered / total) * 100);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Monthly Tests
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/tests/create')}
        >
          Create Test
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Tests
              </Typography>
              <Typography variant="h5">{totalCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Published
              </Typography>
              <Typography variant="h5" color="success.main">
                {tests.filter(t => t.status === 'PUBLISHED').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                In Progress
              </Typography>
              <Typography variant="h5" color="warning.main">
                {tests.filter(t => t.status === 'OPEN').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Drafts
              </Typography>
              <Typography variant="h5" color="text.secondary">
                {tests.filter(t => t.status === 'DRAFT').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setFilterDialogOpen(true)}
              >
                Filters
              </Button>
              {(filterStatus || filterClass || filterAcademicYear) && (
                <Button
                  variant="text"
                  onClick={() => {
                    setFilterStatus('');
                    setFilterClass('');
                    setFilterAcademicYear('');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Test Name</TableCell>
              <TableCell>Class & Section</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Test Date</TableCell>
              <TableCell align="center">Max Marks</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Progress</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : tests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No tests found
                </TableCell>
              </TableRow>
            ) : (
              tests.map((test) => (
                <TableRow key={test.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {test.testName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {test.academicYear}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {test.className} - {test.sectionName}
                  </TableCell>
                  <TableCell>{test.subjectName}</TableCell>
                  <TableCell>
                    {format(new Date(test.testDate), 'dd MMM yyyy')}
                  </TableCell>
                  <TableCell align="center">{test.maxMarks}</TableCell>
                  <TableCell align="center">
                    {getStatusChip(test.status)}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={`${test.marksEntered}/${test.totalStudents} students`}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          {getProgressPercentage(test.marksEntered, test.totalStudents)}%
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, test)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          navigate(`/tests/${selectedTest?.id}`);
          handleMenuClose();
        }}>
          <ViewIcon sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItem>
        <MenuItem onClick={() => {
          navigate(`/tests/${selectedTest?.id}/edit`);
          handleMenuClose();
        }}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem onClick={() => {
          navigate(`/tests/${selectedTest?.id}/marks`);
          handleMenuClose();
        }}>
          <AssessmentIcon sx={{ mr: 1 }} fontSize="small" />
          Enter Marks
        </MenuItem>
        <MenuItem onClick={() => {
          navigate(`/tests/${selectedTest?.id}/files`);
          handleMenuClose();
        }}>
          <PdfIcon sx={{ mr: 1 }} fontSize="small" />
          Manage Files
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{selectedTest?.testName}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Filter Tests</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="DRAFT">Draft</MenuItem>
                  <MenuItem value="OPEN">Open</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="PUBLISHED">Published</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Academic Year</InputLabel>
                <Select
                  value={filterAcademicYear}
                  onChange={(e) => setFilterAcademicYear(e.target.value)}
                  label="Academic Year"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="2024-2025">2024-2025</MenuItem>
                  <MenuItem value="2023-2024">2023-2024</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Class</InputLabel>
                <Select
                  value={filterClass}
                  onChange={(e) => setFilterClass(e.target.value)}
                  label="Class"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="1">Class 10</MenuItem>
                  <MenuItem value="2">Class 11</MenuItem>
                  <MenuItem value="3">Class 12</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setFilterDialogOpen(false);
              fetchTests();
            }}
            variant="contained"
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MonthlyTestList;